import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '@/features/auth/authService';
import type { NavItem } from './buildNavItems';
import {
  clearAtalhosLocal,
  filterSidebarAtalhos,
  loadAtalhos,
  resolveFavoritos,
  toggleCodigoNaOrdem,
} from './atalhosStorage';

type UseNavAtalhosParams = {
  idUsuario: number | undefined;
  idEscola: number | null | undefined;
  /** Valor de `GET /me` — `null` = nunca personalizou. */
  atalhosFromApi: number[] | null | undefined;
  allItems: NavItem[];
  /** Atualiza cache local de `/me` apos persistir. */
  onAtalhosChange?: (codigos: number[]) => void;
};

const REORDER_DEBOUNCE_MS = 300;

export function useNavAtalhos({
  idUsuario,
  idEscola,
  atalhosFromApi,
  allItems,
  onAtalhosChange,
}: UseNavAtalhosParams) {
  const todosCodigos = useMemo(
    () => allItems.map((item) => item.codigoModulo),
    [allItems],
  );

  const [stored, setStored] = useState<number[] | null>(null);
  const personalizadoRef = useRef(false);
  const reorderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    personalizadoRef.current = false;
  }, [idUsuario, idEscola]);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (idUsuario == null || idEscola == null) {
        setStored(null);
        return;
      }

      if (atalhosFromApi != null) {
        personalizadoRef.current = true;
        setStored(atalhosFromApi);
        clearAtalhosLocal(idUsuario, idEscola);
        return;
      }

      // API null: migrar localStorage uma vez, se houver.
      const local = loadAtalhos(idUsuario, idEscola);
      if (local !== null) {
        personalizadoRef.current = true;
        if (!cancelled) setStored(local);
        try {
          await authService.salvarAtalhos(local);
          clearAtalhosLocal(idUsuario, idEscola);
          onAtalhosChange?.(local);
        } catch {
          /* mantem local ate a proxima tentativa */
        }
        return;
      }

      if (!personalizadoRef.current && !cancelled) {
        setStored(null);
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [atalhosFromApi, idEscola, idUsuario, onAtalhosChange]);

  useEffect(() => {
    return () => {
      if (reorderTimerRef.current != null) {
        clearTimeout(reorderTimerRef.current);
      }
    };
  }, []);

  const favoritos = useMemo(
    () => resolveFavoritos(stored, todosCodigos),
    [stored, todosCodigos],
  );

  const sidebarItems = useMemo(
    () => filterSidebarAtalhos(allItems, stored),
    [allItems, stored],
  );

  const persist = useCallback(
    async (codigos: number[]) => {
      if (idUsuario == null || idEscola == null) return;
      personalizadoRef.current = true;
      setStored(codigos);
      onAtalhosChange?.(codigos);
      await authService.salvarAtalhos(codigos);
    },
    [idEscola, idUsuario, onAtalhosChange],
  );

  const isFavorito = useCallback(
    (codigoModulo: number) => favoritos.has(codigoModulo),
    [favoritos],
  );

  const toggleFavorito = useCallback(
    (codigoModulo: number) => {
      if (idUsuario == null || idEscola == null) return;

      const base =
        stored !== null ? stored.filter((c) => todosCodigos.includes(c)) : [...todosCodigos];
      const next = toggleCodigoNaOrdem(base, codigoModulo);
      void persist(next);
    },
    [idEscola, idUsuario, persist, stored, todosCodigos],
  );

  const reordenarAtalhos = useCallback(
    (codigosOrdenados: number[]) => {
      if (idUsuario == null || idEscola == null) return;
      personalizadoRef.current = true;
      setStored(codigosOrdenados);
      onAtalhosChange?.(codigosOrdenados);

      if (reorderTimerRef.current != null) {
        clearTimeout(reorderTimerRef.current);
      }
      reorderTimerRef.current = setTimeout(() => {
        void authService.salvarAtalhos(codigosOrdenados).catch(() => {
          /* UI ja refletiu; proximo toggle/reorder reenvia */
        });
      }, REORDER_DEBOUNCE_MS);
    },
    [idEscola, idUsuario, onAtalhosChange],
  );

  return {
    favoritos,
    sidebarItems,
    isFavorito,
    toggleFavorito,
    reordenarAtalhos,
  };
}
